import { effectiveAccessoryMode, pickOptionForSize, resolveLinkedProductHandle } from "./normalize";
import { getRecommendations } from "./engine";
import {
  buildDesiredSelection,
  desiredSelectionWithMainVariant,
  isBundleTokenInVariant,
  resolveAccessoryItem,
  resolveMainVariant,
} from "./variantResolver";
import { resolveVariantImageSrc } from "./variantImage";
import type {
  BeraterAnswer,
  BeraterData,
  BeraterOptionData,
  BeraterStepData,
  DesiredAccessory,
  DesiredSelection,
  ShopifyJsProduct,
  ShopifyJsVariant,
} from "./types";

export interface AccessoryPreviewImage {
  src: string;
  srcset: string;
  alt: string;
  source: string;
}

export interface AccessoryPreviewItem {
  optionValue: string;
  linkedHandle: string | null;
  image: AccessoryPreviewImage | null;
}

export interface AccessoryPreviewResult {
  productHandle: string;
  items: AccessoryPreviewItem[];
}

interface ResolvedImageSource {
  product: ShopifyJsProduct;
  variant: ShopifyJsVariant | null;
}

export interface PrepareAccessoryOptions {
  /** Skip inject (prefetch only). */
  warmOnly?: boolean;
  /** Drop cached prepare for this step + answers. */
  forceRefresh?: boolean;
}

const productCache = new Map<string, Promise<ShopifyJsProduct | null>>();
const preparedSteps = new Map<string, Promise<AccessoryPreviewResult | null>>();
let prepareGeneration = 0;

const debugLog = (data: BeraterData, label: string, payload: unknown): void => {
  if (!data.debug) return;
  console.log(`[ek-berater preview] ${label}`, payload);
};

const prepareKey = (data: BeraterData, answers: BeraterAnswer[], stepHandle: string): string =>
  `${data.flowHandle}:${stepHandle}:${JSON.stringify(answers)}`;

export const invalidateAccessoryPreviewCache = (): void => {
  preparedSteps.clear();
  prepareGeneration += 1;
};

const fetchProductJs = async (handle: string): Promise<ShopifyJsProduct | null> => {
  if (!handle) return null;
  const cached = productCache.get(handle);
  if (cached) return cached;

  const promise = (async (): Promise<ShopifyJsProduct | null> => {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return (await response.json()) as ShopifyJsProduct;
    } catch {
      return null;
    }
  })();

  productCache.set(handle, promise);
  return promise;
};

const imageWithWidth = (src: string, width: number): string => {
  if (!src) return src;
  const normalized = src.startsWith("//") ? `https:${src}` : src;
  return normalized.includes("?") ? `${normalized}&width=${width}` : `${normalized}?width=${width}`;
};

const buildSrcset = (src: string): string => {
  if (!src) return "";
  return `${imageWithWidth(src, 150)} 150w, ${imageWithWidth(src, 300)} 300w`;
};

const preloadImage = (src: string): Promise<void> =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });

const pickImage = (source: ResolvedImageSource): AccessoryPreviewImage | null => {
  const { product, variant } = source;
  const src = resolveVariantImageSrc(product, variant);
  if (!src) return null;
  return {
    src: imageWithWidth(src, 300),
    srcset: buildSrcset(src),
    alt: product.title,
    source: `${product.handle} / ${variant?.title ?? "default"}`,
  };
};

const desiredWithoutStepAccessories = (
  data: BeraterData,
  answers: BeraterAnswer[],
  step: BeraterStepData,
): DesiredSelection => {
  const desired = buildDesiredSelection(data, answers);
  const stepTokens = new Set(step.options.map((option) => option.value));
  return {
    ...desired,
    accessories: desired.accessories.filter((accessory) => !stepTokens.has(accessory.token)),
  };
};

const resolvePreviewImage = async (
  product: ShopifyJsProduct,
  desired: DesiredSelection,
  mainVariant: ShopifyJsVariant | null,
  option: BeraterOptionData,
): Promise<{ image: AccessoryPreviewImage | null; source: ResolvedImageSource | null }> => {
  const sizedDesired = desiredSelectionWithMainVariant(product, desired, mainVariant);
  const mode = effectiveAccessoryMode(option);

  if (mode === "none") {
    const outcome = resolveMainVariant(product, sizedDesired);
    const source: ResolvedImageSource = { product, variant: outcome.item?.variant ?? null };
    return { image: pickImage(source), source };
  }

  if (mode === "separate_product") {
    const linkedHandle = resolveLinkedProductHandle(option);
    if (!linkedHandle) return { image: null, source: null };
    const linked = await fetchProductJs(linkedHandle);
    if (!linked) return { image: null, source: null };
    const accessory: DesiredAccessory = { token: option.value, mode: "separate_product", linkedProductHandle: null };
    const item = resolveAccessoryItem(linked, accessory, sizedDesired);
    const source: ResolvedImageSource = { product: linked, variant: item?.variant ?? null };
    return { image: pickImage(source), source };
  }

  if (mode === "bundle_variant" || mode === "addon_variant") {
    // Two shapes of same-product accessory exist:
    //  - addon_variant: a standalone Variante row that is NOT a complete bed
    //    (Etagenbett "Schublade"/"Rausfallschutz") — resolved as its own variant.
    //    resolveMainVariant only picks complete beds, so it can't serve these.
    //  - bundle_variant: the accessory is baked into a complete-bed Variante value
    //    ("Hausbett mit Bettkasten" / "… mit 2 Schubladen"). The addon path's
    //    !isCompleteBed filter strips exactly these, leaving an empty pool — so we
    //    fall through to resolveMainVariant carrying the bundle token (same as the
    //    result card). Previews may show sold-out accessories.
    const accessory: DesiredAccessory = { token: option.value, mode: "addon_variant", linkedProductHandle: null };
    const item = resolveAccessoryItem(product, accessory, sizedDesired, { allowSoldOut: true });
    if (item) {
      const source: ResolvedImageSource = { product, variant: item.variant };
      return { image: pickImage(source), source };
    }

    const bundleDesired: DesiredSelection = {
      ...sizedDesired,
      accessories: [{ token: option.value, mode: "bundle_variant", linkedProductHandle: null }],
    };
    const variant = resolveMainVariant(product, bundleDesired).item?.variant ?? null;
    if (variant && isBundleTokenInVariant(product, variant, option.value)) {
      const source: ResolvedImageSource = { product, variant };
      return { image: pickImage(source), source };
    }
    return { image: null, source: null };
  }

  return { image: null, source: null };
};

export const buildAccessoryPreviews = async (
  data: BeraterData,
  answers: BeraterAnswer[],
  step: BeraterStepData,
): Promise<AccessoryPreviewResult | null> => {
  const recommendations = getRecommendations(data, answers);
  debugLog(data, "recommendations", { answers, recommendations });
  if (!recommendations.length) return null;

  const product = await fetchProductJs(recommendations[0].handle);
  debugLog(data, "top candidate", { handle: recommendations[0].handle, product });
  if (!product) return null;

  const desired = buildDesiredSelection(data, answers);
  debugLog(data, "desired selection", desired);

  const items: AccessoryPreviewItem[] = [];
  const baseDesired = desiredWithoutStepAccessories(data, answers, step);

  for (const option of step.options) {
    const picked = pickOptionForSize(step.options, option.value, desired.size);
    if (picked && picked !== option && step.options.filter((item) => item.value === option.value).length > 1) {
      continue;
    }
    const previewDesired = baseDesired;
    const mainVariant = resolveMainVariant(product, previewDesired).item?.variant ?? null;
    const resolved = await resolvePreviewImage(product, previewDesired, mainVariant, option);
    debugLog(data, "option preview", {
      value: option.value,
      mode: option.accessoryMode,
      source: resolved.source
        ? { product: resolved.source.product.handle, variant: resolved.source.variant?.title ?? null }
        : null,
      image: resolved.image?.source ?? null,
    });
    items.push({
      optionValue: option.value,
      linkedHandle: resolveLinkedProductHandle(option),
      image: resolved.image,
    });
  }

  return { productHandle: product.handle, items };
};

const applyImage = (container: HTMLElement, image: AccessoryPreviewImage): void => {
  const fallback = container.querySelector<HTMLImageElement>("[data-berater-preview-fallback]");
  let img = container.querySelector<HTMLImageElement>("img:not([data-berater-preview-fallback])");
  if (!img) {
    if (fallback) {
      img = fallback.cloneNode(true) as HTMLImageElement;
      img.removeAttribute("data-berater-preview-fallback");
      container.appendChild(img);
    } else {
      img = document.createElement("img");
      img.loading = "eager";
      img.sizes = "150px";
      img.className = "w-full h-full object-contain";
      container.appendChild(img);
    }
  }
  if (fallback) fallback.classList.add("hidden");
  img.src = image.src;
  img.srcset = image.srcset;
  img.alt = image.alt;
  img.width = 300;
  img.height = 360;
  img.classList.remove("hidden");
};

const resetPreviewContainers = (panel: HTMLElement): void => {
  for (const container of Array.from(panel.querySelectorAll<HTMLElement>("[data-berater-option-preview-image]"))) {
    for (const img of Array.from(container.querySelectorAll<HTMLImageElement>("img:not([data-berater-preview-fallback])"))) {
      img.remove();
    }
    const fallback = container.querySelector<HTMLImageElement>("[data-berater-preview-fallback]");
    fallback?.classList.remove("hidden");
  }
};

const hideSizeMismatchedDuplicates = (panel: HTMLElement, step: BeraterStepData, size: string | null): void => {
  for (const input of Array.from(panel.querySelectorAll<HTMLInputElement>("[data-berater-option]"))) {
    const card = input.closest<HTMLElement>("[data-berater-option-card]");
    if (!card) continue;
    const value = input.dataset.value ?? input.value;
    if (step.options.filter((option) => option.value === value).length < 2) {
      card.classList.remove("hidden");
      continue;
    }
    const picked = pickOptionForSize(step.options, value, size);
    const keep = Boolean(picked && (input.dataset.linkedProduct || "") === (picked.linkedProductHandle || ""));
    card.classList.toggle("hidden", !keep);
    if (!keep) input.checked = false;
  }
};

const injectPreviewImages = (root: HTMLElement, result: AccessoryPreviewResult, step: BeraterStepData, size: string | null): void => {
  const panel = root.querySelector<HTMLElement>(`[data-berater-panel="${step.handle}"]`);
  if (!panel) return;

  resetPreviewContainers(panel);
  hideSizeMismatchedDuplicates(panel, step, size);

  for (const item of result.items) {
    if (!item.image) continue;
    const valueSelector = `[data-berater-option][data-value="${CSS.escape(item.optionValue)}"]`;
    const input = item.linkedHandle
      ? panel.querySelector<HTMLInputElement>(`${valueSelector}[data-linked-product="${CSS.escape(item.linkedHandle)}"]`)
      : panel.querySelector<HTMLInputElement>(valueSelector);
    if (!input) continue;

    const card = input.closest<HTMLElement>("[data-berater-option-card]");
    if (!card) continue;

    const container = card.querySelector<HTMLElement>("[data-berater-option-preview-image]");
    if (!container) continue;

    applyImage(container, item.image);
  }
};

/** Fetch + preload accessory preview images; inject when the step panel is active. */
export const prepareAccessoryStep = (
  root: HTMLElement,
  data: BeraterData,
  answers: BeraterAnswer[],
  stepHandle: string,
  options: PrepareAccessoryOptions = {},
): Promise<void> => {
  const step = data.steps.find((item) => item.handle === stepHandle);
  if (!step || step.attribute !== "accessories") return Promise.resolve();

  const key = prepareKey(data, answers, stepHandle);
  if (options.forceRefresh) {
    preparedSteps.delete(key);
  }

  const generation = prepareGeneration;
  let buildPromise = preparedSteps.get(key);
  if (!buildPromise) {
    buildPromise = buildAccessoryPreviews(data, answers, step);
    preparedSteps.set(key, buildPromise);
  }

  return (async (): Promise<void> => {
    const result = await buildPromise;
    if (!result || generation !== prepareGeneration) return;

    const urls = [...new Set(result.items.flatMap((item) => (item.image ? [item.image.src] : [])))];
    await Promise.all(urls.map((url) => preloadImage(url)));
    if (generation !== prepareGeneration || options.warmOnly) return;

    injectPreviewImages(root, result, step, buildDesiredSelection(data, answers).size);
  })();
};
